import {
  Group,
  Title,
  Text,
  Flex,
  Divider,
  Tooltip,
  Button,
  Select,
} from "@mantine/core";
import {
  IconBinoculars,
  IconCpu,
  IconReceipt2,
  IconHeartFilled,
  IconBellFilled,
} from "@tabler/icons-react";
import { useTheme, CardInfo } from "../contexts/ThemeContext";

interface PageHeaderProps {
  onAlertClick: () => void;
  onGpuChange?: (card: string) => void;
}

// Helper to render display name with highlighted portion
const HighlightedCardName = ({
  card,
  useThemeColor = false,
}: {
  card: CardInfo;
  useThemeColor?: boolean;
}) => {
  const { display_name, highlight, theme } = card;
  const highlightColor = useThemeColor
    ? "var(--theme-primary)"
    : theme === "green"
      ? "#52c376"
      : "#ff4040";

  // Split the display name around the highlight
  const highlightIndex = display_name.indexOf(highlight);
  if (highlightIndex === -1) {
    return <span style={{ fontWeight: 700 }}>{display_name}</span>;
  }

  const before = display_name.slice(0, highlightIndex);
  const after = display_name.slice(highlightIndex + highlight.length);

  return (
    <>
      {before && <span style={{ color: "white", fontWeight: 700 }}>{before}</span>}
      <span style={{ color: highlightColor, fontWeight: 700 }}>
        {highlight}
      </span>
      {after && <span style={{ color: "white", fontWeight: 700 }}>{after}</span>}
    </>
  );
};

export const PageHeader = ({ onAlertClick, onGpuChange }: PageHeaderProps) => {
  const { gpuCard, setGpuCard, cards, currentCard, theme } = useTheme();

  const handleGpuChange = (value: string | null) => {
    if (value && cards.some((c) => c.model_id === value)) {
      setGpuCard(value);
      if (onGpuChange) {
        onGpuChange(value);
      }
    }
  };

  // Build dropdown data from cards, grouped by brand (green first, then red), sorted A-Z within each group
  // For memory variants (8GB/16GB), 8GB comes before 16GB
  const sortedCards = [...cards].sort((a, b) => {
    // Green (NVIDIA) first, then Red (AMD)
    if (a.theme !== b.theme) {
      return a.theme === "green" ? -1 : 1;
    }
    // Extract base name and memory size for comparison
    const memoryRegex = /^(.+?)\s*(\d+)GB$/i;
    const matchA = a.display_name.match(memoryRegex);
    const matchB = b.display_name.match(memoryRegex);

    // If both have memory suffixes and same base name, sort by memory size (smaller first)
    if (matchA && matchB && matchA[1] === matchB[1]) {
      return parseInt(matchA[2]) - parseInt(matchB[2]);
    }

    // Otherwise sort alphabetically
    return a.display_name.localeCompare(b.display_name);
  });

  const selectData = sortedCards.map((c) => ({
    value: c.model_id,
    label: c.display_name,
  }));

  // Calculate widths: input fits current selection, dropdown fits longest option
  const currentLength = currentCard?.display_name.length || 10;
  const maxLength = Math.max(...cards.map((c) => c.display_name.length), 10);
  const inputWidth = `${currentLength * 0.95}ch`;
  const dropdownWidth = `${maxLength + 2}ch`;

  return (
    <>
      <Group gap="md" mb={8} className="page-header-icons">
        <IconBinoculars
          size={24}
          color="#00ff41"
          style={{
            filter: `brightness(1.2) drop-shadow(0 0 4px #00ff41) drop-shadow(0 0 8px #00ff41) drop-shadow(0 0 16px #00ff4130)`,
          }}
        />
        <IconCpu
          size={24}
          color="#0ff0fc"
          style={{
            filter: `brightness(0.9) drop-shadow(0 0 4px #0ff0fc) drop-shadow(0 0 8px #0ff0fc) drop-shadow(0 0 16px #0ff0fc30)`,
          }}
        />
        <IconReceipt2
          size={24}
          color="#bc13fe"
          style={{
            filter: `brightness(1.4) drop-shadow(0 0 4px #bc13fe) drop-shadow(0 0 8px #bc13fe) drop-shadow(0 0 16px #bc13fe30)`,
          }}
        />
        <IconHeartFilled
          size={24}
          color="#ff006e"
          style={{
            filter: `brightness(1.1) drop-shadow(0 0 4px #ff006e) drop-shadow(0 0 8px #ff006e) drop-shadow(0 0 16px #ff006e30)`,
          }}
        />
      </Group>

      <Flex justify="space-between" align="flex-end" mb="sm">
        <div>
          <Title order={1} fw={600} c="white" className="hoverable-text-title">
            Is it GPU o'Clock?
          </Title>
          <Text size="lg" c="dimmed" mt={4}>
            How much is a{" "}
            <span style={{ display: "inline-block", position: "relative" }}>
              <Select
                value={gpuCard}
                onChange={handleGpuChange}
                data={selectData}
                size="sm"
                className="gpu-selector-input"
                renderOption={({ option }) => {
                  const card = cards.find((c) => c.model_id === option.value);
                  if (!card) return <Text size="sm">{option.label}</Text>;
                  return (
                    <Text size="sm">
                      <HighlightedCardName card={card} />
                    </Text>
                  );
                }}
                comboboxProps={{
                  width: dropdownWidth,
                  position: "bottom-start",
                }}
                maxDropdownHeight={600}
                styles={{
                  input: {
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: "2px dotted rgba(255, 255, 255, 0.3)",
                    borderRadius: 0,
                    background: "transparent",
                    padding: "0 4px",
                    paddingRight: "20px",
                    textAlign: "center",
                    display: "inline-block",
                    width: inputWidth,
                    fontWeight: 700,
                    fontSize: "1.125rem",
                    color: "transparent",
                  },
                  root: {
                    display: "inline-block",
                  },
                  section: {
                    display: "none",
                  },
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translateX(-50%) translateY(-50%)",
                  pointerEvents: "none",
                  fontWeight: 700,
                  fontSize: "1.125rem",
                  whiteSpace: "nowrap",
                }}
              >
                {currentCard ? (
                  <HighlightedCardName card={currentCard} useThemeColor />
                ) : (
                  <span style={{ color: "white" }}>...</span>
                )}
              </span>
            </span>{" "}
            in{" "}
            <Tooltip
              label="Continuously updated with prices from Inet, Webhallen, Amazon, Komplett, and more..."
              position="bottom"
              multiline
              w={300}
            >
              <span style={{ color: "white", fontWeight: 700, cursor: "help" }}>
                Sweden
              </span>
            </Tooltip>{" "}
            right now? 🇸🇪
          </Text>
        </div>
        <Button
          leftSection={<IconBellFilled size={18} />}
          variant="light"
          color={theme === "green" ? "nvidiaGreen" : "amdRed"}
          onClick={onAlertClick}
          className="header-alert-button-desktop"
        >
          Alert me
        </Button>
      </Flex>

      <Divider />
    </>
  );
};
